import { Cart, CartLifeCycle, CartSelectors, OrderTypes, SiloDistributionRequirements, ValidationMessage, ValidationMessageSource } from '@next/core-lib/cart';
import { LocaleService } from '@next/core-lib/i18n';
import { createFeatureSelector, createSelector } from '@ngrx/store';

import { AppState, ValidationFeedback } from '../models/app.models';

// @ts-ignore
export const selectCartState = createFeatureSelector<AppState>('app');

export const selectCartWithHasRemarksValidationsSilosOrSupplements = (cartId: number) =>
  createSelector(CartSelectors.selectCarts, (carts: Cart[]) => {
    if (!carts?.length) {
      return null;
    }
    const cart: Cart = carts.find((entry) => entry.id === cartId);
    if (!cart) {
      return null;
    }
    let lines = cart.lines?.map((line) => ({
      ...line,
      hasRemarksOrValidationErrors: line?.remarks || line.validationMessages?.length,
      hasRemarksValidationsSilosOrSupplements:
        line?.remarks ||
        line.validationMessages?.length ||
        line.orderingInformation?.siloDistributionRequirements !== SiloDistributionRequirements.notRelevant ||
        line.hasProductSupplements,
    }));

    if (cart.type === OrderTypes.preSalesOrder) {
      lines = lines.sort((lineA, lineB) => {
        const dateFromAndNameA = `
          ${lineA.deliveryInformation?.deliverySalesOrderPeriod?.dateFrom}-${lineA.deliveryInformation?.deliverySalesOrderPeriod?.name}
        `;
        const dateFromAndNameB = `
          ${lineB.deliveryInformation?.deliverySalesOrderPeriod?.dateFrom}-${lineB.deliveryInformation?.deliverySalesOrderPeriod?.name}
        `;

        return dateFromAndNameA.localeCompare(dateFromAndNameB);
      });
    }

    return {
      ...cart,
      lines,
    };
  });

export const selectCartValidationMessages = (cartId: number, locale: LocaleService) =>
  createSelector(CartSelectors.selectCarts, (carts: Cart[]) => {
    if (!carts?.length) {
      return null;
    }

    const cart: Cart = carts.find((entry) => entry.id === cartId);
    if (!cart?.validationMessages?.length) {
      return null;
    }

    const messages = [...cart.validationMessages.map((message) => translateValidationMessages(message, locale))];

    return messages.length === 0 ? null : messages;
  });

export const selectNotReadyForSimulation = (cartId: number) =>
  createSelector(CartSelectors.selectCarts, (carts: Cart[]) => {
    if (!carts?.length) {
      return null;
    }

    const cart = carts.find((entry) => entry.id === cartId);
    if (!cart) {
      return null;
    }

    return cart.status !== CartLifeCycle.readyForSubmission;
  });

export const selectHasValidationMessages = (cartId: number) =>
  createSelector(CartSelectors.selectCarts, (carts: Cart[]) => {
    if (!carts?.length) {
      return null;
    }

    const cart = carts.find((entry) => entry.id === cartId);
    if (!cart) {
      return null;
    }

    const orderServiceMessages = cart.validationMessages?.find((message) =>
      [ValidationMessageSource.service, ValidationMessageSource.SAP].includes(message.source),
    );

    if (orderServiceMessages === undefined) {
      return false;
    }

    return cart.validationMessages?.length > 0 || cart.lines.map((line) => line.validationMessages).reduce((a, b) => a.concat(b)).length > 0;
  });

const getValidationMessageIdentifier = (message: ValidationMessage): string => {
  const sapMessage = message.context?.['Message'];
  const isDistrictVolumeBlock = message.errorCode === 'V1028' && typeof sapMessage === 'string' && sapMessage.toLowerCase().includes('district vol. block');

  return isDistrictVolumeBlock ? 'cart-feedback.CV-5002' : `cart-feedback.${message.errorCode}`;
};

export const translateValidationMessages = (message: ValidationMessage, locale: LocaleService) => {
  const messageIdentifier = getValidationMessageIdentifier(message);
  let localizedMessage = locale.translate(messageIdentifier);

  if (!message.errorCode || localizedMessage === messageIdentifier) {
    return {
      message: locale.translate('cart-feedback.GE-0001'),
      severityLevel: message.severityLevel,
      errorCode: message?.errorCode,
    } as ValidationFeedback;
  }

  // Handle cases where ContextVariables are not set (e.g., CV-2012 errors)
  // This prevents 500 errors when order-aggregation fails to retrieve invoiceLines
  if (message.context && typeof message.context === 'object') {
    const fields = Object.keys(message.context);

    fields.forEach((element) => {
      localizedMessage = localizedMessage.replace(`{${element}}`, (message.context?.[element] as string) ?? '');
    });
  }

  return {
    message: localizedMessage,
    severityLevel: message.severityLevel,
    errorCode: message?.errorCode,
  } as ValidationFeedback;
};
